/**
 * Decides whether the play sheet is allowed to change anything right now.
 *
 * TWO SEPARATE PROBLEMS, ONE ANSWER.
 *
 * **In flight.** React state is not a same-tick lock. `handleAttack` used to
 * check `attacking`, but that value only changes once React has processed
 * `setAttacking(true)`, which is at least one tick after the click. Two clicks
 * inside that window both pass the check, both roll, and both resolve from the
 * same records - and `CheckpointCommitter` then refuses the second, so the
 * player has spent a roll they will never see land. Claiming ownership here is
 * synchronous, so the second click never reaches the RNG at all.
 *
 * **Recovery required.** A commit that fails may leave a pending journal. The
 * records the sheet is holding are then no longer authoritative: the journal
 * describes a transaction that is still going to land. Several pathways -
 * ordinary Checks, Evade, damage, conditions, passing time, inventory - save
 * through their own repositories and know nothing about the journal, so without
 * a shared answer the player can keep working and have that work overwritten
 * when recovery replays the older snapshot.
 *
 * This is DELIBERATELY NOT React state. It is a plain object held in a ref, so
 * every handler reads the same value in the same tick it was set. The screen
 * mirrors it into state purely so buttons can go grey; the grey buttons are an
 * explanation, and this is the boundary.
 *
 * It contains no game rules. It is a lock, and it lives in `src/app/` so it can
 * be tested without a renderer.
 */

export type MutationBlockedKind =
  /** An owned operation is committing. Wait, then try again. */
  | 'busy'
  /** A save was interrupted. Only a reload clears this. */
  | 'recovery-required';

export interface MutationBlocked {
  kind: MutationBlockedKind;
  /** Shown to the player as-is. */
  message: string;
}

const BUSY: MutationBlocked = {
  kind: 'busy',
  message: 'Another change is still saving. Wait for it to finish before making another.',
};

const RECOVERY_REQUIRED: MutationBlocked = {
  kind: 'recovery-required',
  message:
    'The save was interrupted. Reload this crawler to finish recovery before making more changes.',
};

export class SheetMutationGate {
  private inFlight = false;
  private recoveryRequired = false;

  /**
   * Why nothing may be written right now, or null when it may.
   *
   * EVERY mutating pathway on the sheet asks this first, including the ones
   * that only fire a small fire-and-forget save. A forgotten new control is the
   * failure mode this exists to survive, so the check belongs at the shared
   * save helpers rather than on each button.
   */
  blocked(): MutationBlocked | null {
    // Recovery outranks busy: it is the one the player has to act on, and it
    // does not clear itself.
    if (this.recoveryRequired) return RECOVERY_REQUIRED;
    if (this.inFlight) return BUSY;
    return null;
  }

  /**
   * Takes ownership for an operation that will commit, or explains why it may
   * not. SYNCHRONOUS: the claim is visible to the next call immediately, which
   * is the entire point.
   */
  claim(): MutationBlocked | null {
    const refusal = this.blocked();
    if (refusal !== null) return refusal;
    this.inFlight = true;
    return null;
  }

  /**
   * Gives ownership back. Called from `finally`, so a thrown error cannot leave
   * the sheet locked. It deliberately does NOT clear a recovery requirement:
   * the operation that failed is exactly the one that raised it.
   */
  release(): void {
    this.inFlight = false;
  }

  /**
   * A save may have left a pending journal. Sticky until the crawler is
   * reloaded, because nothing else can prove the journal has landed.
   */
  requireRecovery(): void {
    this.recoveryRequired = true;
  }

  /**
   * ONLY the reload pathway calls this, and only after `recover()` has run and
   * all three records have been read again. Clearing it anywhere else would
   * hand the player back a sheet still holding pre-recovery values.
   */
  clearRecovery(): void {
    this.recoveryRequired = false;
  }

  get isBusy(): boolean {
    return this.inFlight;
  }

  get needsRecovery(): boolean {
    return this.recoveryRequired;
  }
}
